import React, { useState, useEffect } from "react";
import {
  PoweroffOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  UsergroupAddOutlined,
  FileTextOutlined,
  PictureOutlined,
} from "@ant-design/icons";
import { signOut, createUserWithEmailAndPassword, sendPasswordResetEmail, deleteUser, signInWithEmailAndPassword } from "firebase/auth";
import { auth, db } from "../../firebase/firebase";
import {
  UserSwitchOutlined,
  MedicineBoxOutlined,
  ScanOutlined,
  CheckSquareOutlined,
} from "@ant-design/icons";
import {
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  updateDoc,
  query,
  where,
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { getApp, initializeApp } from "firebase/app";
import { getAuth, initializeAuth, indexedDBLocalPersistence } from "firebase/auth";
import logo from "./../../assets/icon.png";
import styles from "./dashboard.module.css"; // CSS Module import
import {
  Layout,
  Menu,
  Breadcrumb,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Popconfirm,
  message,
  Upload,
  notification,
  Typography,
} from "antd";
import Medecins from "../../components/Medecins";
import Patients from "../../components/Patients";
import Candidates from "../../components/Candidates"; // Import the new Candidates component
import Factures from "../../components/Factures"; // Import the new Factures component
import Eterna from "../../components/Eterna"; // Import the new Eterna component
import Refinement from "../../components/Refinement";

const { Header, Content, Sider } = Layout;

const Dashboard = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [activeMenu, setActiveMenu] = useState("admins"); // Track active menu
  const [uploading, setUploading] = useState(false);
  const [uploadStatusModal, setUploadStatusModal] = useState(false);
  const [uploadStatus, setUploadStatus] = useState({ type: '', message: '' });
  const [bannerUrl, setBannerUrl] = useState(null);

  // Check authentication and fetch admins
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        window.location.href = "/";
        return;
      }
      setCurrentUser(user);
      // Fetch role from Firestore
      const adminsQuery = query(collection(db, "admins"), where("email", "==", user.email));
      const adminsSnapshot = await getDocs(adminsQuery);
      if (!adminsSnapshot.empty) {
        setCurrentUserRole(adminsSnapshot.docs[0].data().role);
      }
    });
    return () => unsubscribe();
  }, []);

  // Fetch admins data
  useEffect(() => {
    /*console.log(currentUser)*/
    const fetchCurrentAdmin = async () => {
      if (!currentUser) return;
      try {
        const adminsQuery = query(
          collection(db, "admins"),
          where("email", "!=", currentUser.email)
        );
        const adminsSnapshot = await getDocs(adminsQuery);
        const adminsList = adminsSnapshot.docs.map((doc) => ({
          key: doc.id,
          ...doc.data(),
        }));
        setAdmins(adminsList);
      } catch (error) {
        /*console.error("Error fetching current admin:", error);*/
      }
    };
    fetchCurrentAdmin();
  }, [currentUser]);

  // Add this useEffect to fetch the current banner URL
  useEffect(() => {
    const fetchBannerUrl = async () => {
      try {
        const storage = getStorage();
        const bannerRef = ref(storage, 'pub/banner');
        const url = await getDownloadURL(bannerRef);
        setBannerUrl(url);
      } catch (error) {
        // Ignore error if file doesn't exist
        if (error.code !== 'storage/object-not-found') {
          /*console.error("Error fetching banner:", error);*/
        }
      }
    };
    fetchBannerUrl();
  }, [uploadStatusModal]); // Refetch when upload status changes

  const showModal = (admin = null) => {
    if (admin) {
      form.setFieldsValue(admin);
      setEditingAdmin(admin);
    } else {
      form.resetFields();
      setEditingAdmin(null);
    }
    setIsModalVisible(true);
  };

  const handleSaveAdmin = async (values) => {
    try {
      if (editingAdmin) {
        // Update existing admin in Firestore
        await updateDoc(doc(db, "admins", editingAdmin.key), {
          name: values.name,
          email: values.email.toLowerCase(), // Ensure consistent email format
          role: values.role,
          phone: values.phone,
          updatedAt: new Date().toISOString()
        });
        
        setAdmins((prev) =>
          prev.map((a) => (a.key === editingAdmin.key ? { 
            ...a, 
            ...values,
            email: values.email.toLowerCase(),
            updatedAt: new Date().toISOString()
          } : a))
        );
        
        message.success("Admin updated successfully");
      } else {
        // Create a separate Firebase app instance for admin creation
        const adminApp = initializeApp({
          apiKey: "AIzaSyA9POaFPDmFWLg2WiBtlZXU7sWlgbSG2E4",
          authDomain: "diamond-project-cf37b.firebaseapp.com",
          projectId: "diamond-project-cf37b",
          storageBucket: "diamond-project-cf37b.appspot.com",
          messagingSenderId: "381060633989",
          appId: "1:381060633989:web:98af578db08a986dc8a0ec",
          measurementId: "G-4VJB3Z508T"
        }, 'admin-creation');
        
        const adminAuth = initializeAuth(adminApp, {
          persistence: indexedDBLocalPersistence
        }, 'admin-auth');
        
        // Create auth user with the password from the form
        const userCredential = await createUserWithEmailAndPassword(
          adminAuth, 
          values.email.toLowerCase(), 
          values.password
        );
        
        const userId = userCredential.user.uid;
        
        // Add to admins collection with the userId
        const adminDoc = await addDoc(collection(db, "admins"), {
          name: values.name,
          email: values.email.toLowerCase(),
          role: values.role,
          phone: values.phone,
          userId: userId,
          createdAt: new Date().toISOString(),
        });
        
        // Update local state
        setAdmins((prev) => [
          ...prev,
          {
            key: adminDoc.id,
            name: values.name,
            email: values.email.toLowerCase(),
            role: values.role,
            phone: values.phone,
            userId: userId,
            createdAt: new Date().toISOString(),
          },
        ]);
        
        message.success("Admin added successfully. They can now log in with their email and password.");
      }
      setIsModalVisible(false);
    } catch (error) {
      console.error("Error saving admin:", error);
      
      if (error.code === 'auth/email-already-in-use') {
        message.error("This email is already registered. Please use a different email.");
      } else if (error.code === 'auth/weak-password') {
        message.error("Password is too weak. Please use a stronger password (at least 6 characters).");
      } else {
        message.error(`Failed to save admin: ${error.message}`);
      }
    }
  };

  const handleDeleteAdmin = async (adminData) => {
    try {
      // Delete from Firestore first
      await deleteDoc(doc(db, "admins", adminData.key));
      
      // Delete from Authentication using the same approach as Medecins.jsx
      const auth = getAuth();
      try {
        // Get the user by email
        const userCredential = await auth.fetchSignInMethodsForEmail(adminData.email);
        if (userCredential.length > 0) {
          // Get the user by UID
          const user = await auth.getUserByEmail(adminData.email);
          if (user) {
            await auth.deleteUser(user.uid);
          }
        }
      } catch (error) {
        console.error("Error deleting user from auth:", error);
        // Continue with the deletion process even if auth deletion fails
      }
      
      // Update local state
      setAdmins((prev) => prev.filter((admin) => admin.key !== adminData.key));
      message.success("Admin deleted successfully");
    } catch (error) {
      console.error("Error deleting admin:", error);
      message.error("Failed to delete admin");
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      window.location.href = "/";
    } catch (error) {
      /*console.error("Logout error:", error);*/
    }
  };

  const handleMenuClick = (key) => {
    switch (key) {
      case "1":
        setActiveMenu("admins");
        break;
      case "2":
        setActiveMenu("medecins");
        break;
      case "3":
        setActiveMenu("patients");
        break;
      case "4":
        setActiveMenu("aligners");
        break;
      case "5":
        setActiveMenu("tpcheck");
        break;
      case "6":
        setActiveMenu("candidates");
        break;
      case "7":
        setActiveMenu("factures");
        break;
      case "8":
        setActiveMenu("eterna");
        break;
      case "9":
        setActiveMenu("pub");
        break;
      case "10":
        setActiveMenu("10");
        break;
      default:
        break;
    }
  };

  const handleImageUpload = async (file) => {
    try {
      setUploading(true);
      const storage = getStorage();
      const bannerRef = ref(storage, 'pub/banner');

      // Delete the existing banner if it exists
      try {
        await deleteObject(bannerRef);
      } catch (error) {
        // Ignore error if file doesn't exist
        if (error.code !== 'storage/object-not-found') {
          throw error;
        }
      }

      // Upload the new banner
      await uploadBytes(bannerRef, file);
      
      setUploadStatus({
        type: 'success',
        message: 'Banner image uploaded successfully!'
      });
      setUploadStatusModal(true);
    } catch (error) {
      setUploadStatus({
        type: 'error',
        message: 'Failed to upload image: ' + error.message
      });
      setUploadStatusModal(true);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteBanner = async () => {
    try {
      const storage = getStorage();
      const bannerRef = ref(storage, 'pub/banner');
      await deleteObject(bannerRef);
      setBannerUrl(null);
      message.success('Banner image deleted successfully');
    } catch (error) {
      if (error.code === 'storage/object-not-found') {
        message.error('No banner image found to delete');
      } else {
        message.error('Failed to delete banner image');
      }
    }
  };

  const renderContent = () => {
    switch (activeMenu) {
      case "admins":
        if (currentUserRole !== "superadmin") return null;
        const columns = [
          {
            title: "Name",
            dataIndex: "name",
            key: "name",
          },
          {
            title: "Email",
            dataIndex: "email",
            key: "email",
          },
          {
            title: "Role",
            dataIndex: "role",
            key: "role",
          },
          {
            title: "Phone",
            dataIndex: "phone",
            key: "phone",
          },
          {
            title: "Actions",
            key: "actions",
            render: (_, record) => (
              <Space size="middle">
                <Button icon={<EditOutlined />} onClick={() => showModal(record)} />
                <Popconfirm
                  title="Are you sure to delete this admin?"
                  onConfirm={() => handleDeleteAdmin(record)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </Space>
            ),
          },
        ];
        return (
          <>
            <Breadcrumb className={styles.breadcrumb} items={[{ title: 'Dashboard' }, { title: 'Admins' }]} />
            <div className={styles.contentInner}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => showModal()}
                className={styles.addButton}
              >
                Add Admin
              </Button>
              <Table
                dataSource={admins}
                columns={columns}
                rowKey="key"
                className={styles.table}
              />
            </div>
          </>
        );
      case "medecins":
        return (
          <>
            <Breadcrumb className={styles.breadcrumb} items={[{ title: 'Dashboard' }, { title: 'Medecins' }]} />
            <Medecins currentUserRole={currentUserRole} />
          </>
        );
      case "patients":
        return (
          <>
            <Breadcrumb className={styles.breadcrumb} items={[{ title: 'Dashboard' }, { title: 'Patients' }]} />
            <Patients currentUserRole={currentUserRole} />
          </>
        );
      case "candidates":
        return (
          <>
            <Breadcrumb className={styles.breadcrumb} items={[{ title: 'Dashboard' }, { title: 'Candidates' }]} />
            <Candidates />
          </>
        );
      case "factures":
        if (currentUserRole !== "superadmin") return null;
        return (
          <>
            <Breadcrumb className={styles.breadcrumb} items={[{ title: 'Dashboard' }, { title: 'Factures' }]} />
            <Factures />
          </>
        );
      case "eterna":
        if (currentUserRole !== "superadmin") return null;
        return (
          <>
            <Breadcrumb className={styles.breadcrumb} items={[{ title: 'Dashboard' }, { title: 'Eterna' }]} />
            <Eterna />
          </>
        );
      case "10":
        if (currentUserRole !== "superadmin") return null;
        return (
          <>
            <Breadcrumb className={styles.breadcrumb} items={[{ title: 'Dashboard' }, { title: 'Refinement' }]} />
            <Refinement />
          </>
        );
      case "pub":
        if (currentUserRole !== "superadmin") return null;
        return (
          <>
            <Breadcrumb className={styles.breadcrumb} items={[{ title: 'Dashboard' }, { title: 'Pub' }]} />
            <div className={styles.contentInner}>
              <div style={{ marginBottom: '20px' }}>
                <Typography.Text strong>Required image dimensions: 1430 x 160 pixels</Typography.Text>
              </div>
              {bannerUrl && (
                <div style={{ marginBottom: '20px' }}>
                  <img 
                    src={bannerUrl} 
                    alt="Current Banner" 
                    style={{ maxWidth: '100%', maxHeight: '300px', marginBottom: '10px' }} 
                  />
                  <Button 
                    danger 
                    icon={<DeleteOutlined />}
                    onClick={handleDeleteBanner}
                  >
                    Delete Banner
                  </Button>
                </div>
              )}
              <Upload
                accept="image/*"
                showUploadList={false}
                beforeUpload={(file) => {
                  handleImageUpload(file);
                  return false;
                }}
              >
                <Button 
                  type="primary" 
                  icon={<PictureOutlined />}
                  loading={uploading}
                  className={styles.addButton}
                >
                  {bannerUrl ? 'Change Banner' : 'Upload Banner'}
                </Button>
              </Upload>
            </div>

            <Modal
              title={uploadStatus.type === 'success' ? 'Success' : 'Error'}
              open={uploadStatusModal}
              onOk={() => setUploadStatusModal(false)}
              onCancel={() => setUploadStatusModal(false)}
              footer={[
                <Button key="ok" type="primary" onClick={() => setUploadStatusModal(false)}>
                  OK
                </Button>
              ]}
            >
              <p>{uploadStatus.message}</p>
            </Modal>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Layout className={styles.layout}>
      {/* Sidebar */}
      <Sider
        className={styles.sidebar}
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
      >
        <div className={styles.logoContainer}>
          <img src={logo} alt="logo" className={styles.logo} />
        </div>
        <Menu
          theme="dark"
          mode="inline"
          defaultSelectedKeys={["1"]}
          className={styles.menu}
          onClick={({ key }) => handleMenuClick(key)}
        >
          {currentUserRole === "superadmin" && (
            <Menu.Item key="1" icon={<UserSwitchOutlined />}>Admins</Menu.Item>
          )}
          <Menu.Item key="2" icon={<MedicineBoxOutlined />}>Medecins</Menu.Item>
          <Menu.Item key="3" icon={<UsergroupAddOutlined />}>Patients</Menu.Item>
          <Menu.Item key="6" icon={<UsergroupAddOutlined />}>Candidates</Menu.Item>
          {currentUserRole === "superadmin" && (
            <>
              <Menu.Item key="7" icon={<FileTextOutlined />}>Factures</Menu.Item>
              <Menu.Item key="8" icon={<ScanOutlined />}>Eterna</Menu.Item>
              <Menu.Item key="10" icon={<ScanOutlined />}>Refinement</Menu.Item>
              <Menu.Item key="9" icon={<PictureOutlined />}>Pub</Menu.Item>
            </>
          )}
        </Menu>
      </Sider>
      {/* Main Content */}
      <Layout className={styles.contentLayout}>
        <Header className={styles.header}>
          <Button
            icon={<PoweroffOutlined />}
            onClick={handleLogout}
            className={styles.logoutButton}
          >
            Logout
          </Button>
        </Header>
        <Content className={styles.content}>
          {renderContent()}
        </Content>
      </Layout>
      {/* Modal */}
      <Modal
        title={editingAdmin ? "Edit Admin" : "Add Admin"}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        className={styles.modal}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveAdmin}
          initialValues={editingAdmin}
          className={styles.form}
        >
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: "Please input the admin's name!" }]}
          >
            <Input placeholder="Enter name" />
          </Form.Item>
          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: "Please input the admin's email!" },
              { type: "email", message: "Please enter a valid email!" },
            ]}
          >
            <Input placeholder="Enter email" />
          </Form.Item>
          <Form.Item
            label="Role"
            name="role"
            rules={[{ required: true, message: "Please select a role!" }]}
          >
            <Select placeholder="Select role">
              <Select.Option value="admin">Admin</Select.Option>
              <Select.Option value="superadmin">Super Admin</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            label="Phone Number"
            name="phone"
            rules={[
              { required: true, message: "Please input the phone number!" },
              { pattern: /^[0-9]+$/, message: "Please enter a valid phone number with only digits!" },
            ]}
          >
            <Input placeholder="Enter phone number" />
          </Form.Item>
          {!editingAdmin && (
            <Form.Item
              label="Password"
              name="password"
              rules={[
                { required: true, message: "Please input the admin's password!" },
                { min: 6, message: "Password must be at least 6 characters long!" },
              ]}
            >
              <Input.Password placeholder="Enter password" />
            </Form.Item>
          )}
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              block
              className={styles.submitButton}
            >
              Save
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
};

export default Dashboard;